import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import { CALENDAR_FORMAT, isDateBeforeToday, isEndDateBeforeStartDate } from "common/calendar";
import AppButton from "components/app-button";
import AppCalendarInput from "components/app-calendar-input";
import placeholderFormat from "common/placeholder-format";
import AppEmployeesListModal from "./app-employees-list-modal";
import AppSelectInput from "components/app-select-input";
import getAllCourseListing from "services/get-all-course-listing";
import AppTable, { AppTableCell } from "components/app-table";
import getReminderTemplateListing from "services/get-reminder-template-listing";

const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

export const AppCreateAssignmentModal = (props, ref) => {
    const dispatch = useDispatch();
    const employeeListRef = useRef();
    const currentDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()), [currentDate]);
    const [visible, setVisible] = useState(false);
    const initialValues = useMemo(() => {
        const data = { id: "", title: "", startDate: "", endDate: "", reminderTemplate: "", selectedEmployees: [] };
        return data;
    }, []);

    //prettier-ignore
    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: yup.object({
            title: yup.string().required(ERRORS.REQUIRED),
            startDate: yup.string().required(ERRORS.REQUIRED).test("beforeEndDate", ERRORS.START_DATE, function (value) { return isDateBeforeToday(value, this.parent.endDate); }),
            endDate: yup.string().required(ERRORS.REQUIRED).test("afterStartDate", ERRORS.END_DATE, function (value) { return isDateBeforeToday(this.parent.startDate || new Date(), value); }),
            reminderTemplate: yup.string().required(ERRORS.required),
            selectedEmployees: yup.array().min(1, ERRORS.MININUM_EMPLOYEE),
        }),
        onSubmit: (values) => {
            onHandleConfirm(values);
        },
    });

    //prettier-ignore
    const onHandleStartDate = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.endDate)) formik.setFieldValue("endDate", "");
    }, [formik]);

    const employeeLabel = formik.values.selectedEmployees.length === 0
        ? "Select Employees"
        : `${formik.values.selectedEmployees.length} employee(s) selected`;

    const data = useMemo(() => ({
        items: formik.values.selectedEmployees,
    }), [formik.values.selectedEmployees]);

    const onHandleShow = useCallback(() => {
        setVisible(true);
    }, []);

    const onHandleDismiss = useCallback(() => {
        formik.resetForm();
        setVisible(false);
    }, [formik]);

    //prettier-ignore
    const onHandleShowEmployeeList = () => {
        employeeListRef.current?.onHandleShow();
    };

    //prettier-ignore
    const onHandleSetEmployees = (list) => {
        formik.setFieldValue("selectedEmployees", list);
    };

    //prettier-ignore
    const onHandleRemoveEmployee = useCallback((employeeId) => {
        const updatedList = formik.values.selectedEmployees.filter(emp => emp.id !== employeeId);
        formik.setFieldValue("selectedEmployees", updatedList);
    }, [formik]);

    //prettier-ignore
    const onHandleConfirm = useCallback(async (values) => {
        let response = false;

        try {
            const payload = {
                courseId: values.title,
                startDate: values.startDate,
                endDate: values.endDate,
                employeeIds: values.selectedEmployees.map(row => row.id),
                reminderTemplateId: values.reminderTemplate,
            };

            response = await api.post.assignment.create(payload);
        } catch (error) {
            serveRequestErrors(error);
            formik.setSubmitting(false);
        }

        if (response) {
            onHandleDismiss();
            dispatch(promptAlertMessage({ message: "Assignment has been created successfully" }));
            props.onHandleGetList();
        }
    }, [formik, dispatch, onHandleDismiss, props]);

    //prettier-ignore
    const NameCell = useCallback(({ row }) => {
        return (
            <div className="name-column">
                <AppTableCell left value={row.original.name} />
            </div>
        );
    }, []);

    //prettier-ignore
    const ActionCell = useCallback(({ row }) => {
        return (
            <div className="action-column">
                <AppButton
                    type="button"
                    label="Remove"
                    outline
                    onClick={() => onHandleRemoveEmployee(row.original.id)}
                />
            </div>
        );
    }, [onHandleRemoveEmployee]);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Id",
            accessor: "employeeId",
            disableSortBy: true,
        },
        {
            Header: "Name",
            accessor: "name",
            disableSortBy: true,
            Cell: NameCell,
        },
        {
            Header: "Email",
            accessor: "email",
            disableSortBy: true,
        },
        {
            Header: "Action",
            accessor: "action",
            disableSortBy: true,
            Cell: ActionCell,
        },
    ], [NameCell, ActionCell]);

    //prettier-ignore
    useImperativeHandle(ref, () => ({
        onHandleShow: onHandleShow,
        onHandleDismiss: onHandleDismiss,
    }));

    return (
        <>
            <Modal classes={{ root: "app-create-assignment-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="create-course-modal" aria-describedby="create-course-modal">
                <div className="create-assignment-modal">
                    <div className="create-assignment-modal__main">
                        <div className="create-assignment-modal__header">
                            <p className="create-assignment-modal__title">Create Assignment</p>
                        </div>

                        <form onSubmit={formik.handleSubmit}>
                            <div className="create-assignment-modal__body">
                                {/* prettier-ignore */}
                                <AppSelectInput required type="text" name="title" label="Title" placeholder="Please Select" loadOptions={getAllCourseListing} value={formik.values.title} error={formik.errors.title} touched={formik.touched.title} disabled={formik.isSubmitting} onChange={formik.handleChange} />

                                {/* prettier-ignore */}
                                <AppCalendarInput required name="startDate" label="Start Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={minDate} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} disabled={formik.isSubmitting} onChange={onHandleStartDate} />

                                {/* prettier-ignore */}
                                <AppCalendarInput required name="endDate" label="End Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.startDate || minDate} value={formik.values.endDate} error={formik.errors.endDate} touched={formik.touched.endDate} disabled={formik.isSubmitting} onChange={formik.setFieldValue} />

                            {/* prettier-ignore */}
                            <AppSelectInput pagination type="text" name="reminderTemplate" label="Reminder Template" placeholder="Select Template" loadOptions={getReminderTemplateListing} value={formik.values.reminderTemplate} error={formik.errors.reminderTemplate} touched={formik.touched.reminderTemplate} disabled={formik.isSubmitting} onChange={formik.handleChange} />
                            
                            {/* prettier-ignore */}
                            <AppButton required onClick={onHandleShowEmployeeList} type="button" label={employeeLabel} />

                                {formik.touched.selectedEmployees && formik.errors.selectedEmployees && (
                                    <p className="create-assignment-modal__error">
                                        {formik.errors.selectedEmployees}
                                    </p>
                                )}

                                {formik.values.selectedEmployees.length > 0 && (
                                    <div className="create-assignment-modal__selected-employees">
                                        <h3>Selected Employees</h3>
                                        <AppTable columns={columns} pages={data} />
                                    </div>
                                )}
                            </div>

                            <div className="create-assignment-modal__button-container">
                                <AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
                                <AppButton type="submit" label="Create" disabled={formik.isSubmitting} />
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>
            <AppEmployeesListModal 
                ref={employeeListRef} 
                onHandleSetEmployees={onHandleSetEmployees}
                currentSelectedEmployees={formik.values.selectedEmployees}
            />
        </>
    );
};

export default memo(forwardRef(AppCreateAssignmentModal));

AppCreateAssignmentModal.propTypes = {
    ref: PropTypes.object,
    onHandleGetList: PropTypes.func.isRequired,
};